import { Button, Modal, Table, Spin, Tag } from "antd"
import React, { useState } from "react"
import { calculateProjectedMeters } from "../../../../components/Orders/Helpers/CalculateProjectedMeters"

export default function MissingPaperSummary({ data, loading, filterStatus }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [calculatingMeters, setCalculatingMeters] = useState(false)
  const [ordersWithMeters, setOrdersWithMeters] = useState([])
  const [selectedGroupOrders, setSelectedGroupOrders] = useState([])
  const [selectedGroupInfo, setSelectedGroupInfo] = useState(null)
  const [isOrderDetailsModalOpen, setIsOrderDetailsModalOpen] = useState(false)

  // Filter data where orderStatus matches the provided filterStatus (default: 2)
  const targetStatus = filterStatus !== undefined ? filterStatus : 2
  const filteredData = data?.filter((item) => item.orderStatus === targetStatus) || []

  const showModal = async () => {
    setIsModalOpen(true)
    setCalculatingMeters(true)

    // Calculate projected meters for all orders and add 50 meters extra
    const ordersWithProjectedMeters = await Promise.all(
      filteredData.map(async (order) => {
        const orderWithMeters = await calculateProjectedMeters(order)
        return {
          ...orderWithMeters,
          projectedMeters: (orderWithMeters.projectedMeters || 0) + 50,
        }
      }),
    )

    setOrdersWithMeters(ordersWithProjectedMeters)
    setCalculatingMeters(false)
  }

  const handleOk = () => {
    setIsModalOpen(false)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  const showOrderDetails = (material, formatWidth) => {
    const orders = ordersWithMeters.filter(
      (order) =>
        (order.selectedProduct?.material || "Unknown Material") === material &&
        (order.selectedProduct?.formatWidth || 0) === formatWidth,
    )
    setSelectedGroupOrders(orders)
    setSelectedGroupInfo({ material, formatWidth })
    setIsOrderDetailsModalOpen(true)
  }

  // Group by material and width
  const groupedData = Object.values(
    ordersWithMeters.reduce((acc, item) => {
      const material = item.selectedProduct?.material || "Unknown Material"
      const formatWidth = item.selectedProduct?.formatWidth || 0
      const key = `${material}_${formatWidth}`

      if (!acc[key]) {
        acc[key] = {
          key,
          material,
          formatWidth,
          totalProjectedMeters: 0,
          orderCount: 0,
        }
      }

      acc[key].totalProjectedMeters += item.projectedMeters || 0
      acc[key].orderCount += 1

      return acc
    }, {}),
  )

  const columns = [
    {
      title: "Material",
      dataIndex: "material",
      key: "material",
      sorter: (a, b) => a.material.localeCompare(b.material),
      defaultSortOrder: "ascend",
    },
    {
      title: "Width (mm)",
      dataIndex: "formatWidth",
      key: "formatWidth",
      width: 150,
      align: "center",
      sorter: (a, b) => a.formatWidth - b.formatWidth,
    },
    {
      title: "Orders",
      dataIndex: "orderCount",
      key: "orderCount",
      width: 100,
      align: "center",
      sorter: (a, b) => a.orderCount - b.orderCount,
      render: (count, record) => (
        <Button
          type="link"
          onClick={() => showOrderDetails(record.material, record.formatWidth)}
          style={{ padding: 0 }}>
          {count}
        </Button>
      ),
    },
    {
      title: "Projected Meters",
      dataIndex: "totalProjectedMeters",
      key: "totalProjectedMeters",
      width: 180,
      align: "right",
      render: (meters) => <strong>{meters.toFixed(2)} m</strong>,
      sorter: (a, b) => a.totalProjectedMeters - b.totalProjectedMeters,
    },
  ]

  const orderDetailsColumns = [
    {
      title: "Order Number",
      dataIndex: "orderNumber",
      key: "orderNumber",
      width: 120,
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      key: "client",
      ellipsis: true,
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      key: "productName",
      ellipsis: true,
    },
    {
      title: "Width",
      dataIndex: ["selectedProduct", "labelWidth"],
      key: "labelWidth",
      width: 80,
      align: "center",
    },
    {
      title: "Height",
      dataIndex: ["selectedProduct", "labelHeight"],
      key: "labelHeight",
      width: 80,
      align: "center",
    },
    {
      title: "Quantity",
      dataIndex: "labelCount",
      key: "labelCount",
      width: 100,
      align: "right",
      render: (qty) => qty?.toLocaleString(),
    },
    {
      title: "Projected Meters",
      dataIndex: "projectedMeters",
      key: "projectedMeters",
      width: 140,
      align: "right",
      render: (meters) => <strong>{meters?.toFixed(2)} m</strong>,
    },
  ]

  return (
    <>
      <style>
        {`
          .always-show-scrollbar .ant-table-body {
            overflow-y: scroll !important;
          }
          .always-show-scrollbar .ant-table-body::-webkit-scrollbar {
            width: 10px;
          }
          .always-show-scrollbar .ant-table-body::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }
          .always-show-scrollbar .ant-table-body::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 10px;
          }
          .always-show-scrollbar .ant-table-body::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        `}
      </style>
      <Button onClick={showModal}>View Required Materials | ({filteredData.length} orders)</Button>
      <Modal
        title={`Materials Needed - ${groupedData.length} Material Groups`}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={800}
        footer={[
          <Button key="close" type="primary" onClick={handleOk}>
            Close
          </Button>,
        ]}>
        {calculatingMeters ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Calculating projected meters...</p>
          </div>
        ) : (
          <Table
            className="always-show-scrollbar"
            columns={columns}
            dataSource={groupedData}
            rowKey="key"
            size="small"
            pagination={false}
            scroll={{ y: 500, scrollToFirstRowOnChange: true }}
            loading={loading}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <strong>Total:</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    <strong>{groupedData.reduce((sum, g) => sum + g.orderCount, 0)} orders</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        )}
      </Modal>

      {/* Order Details Modal */}
      <Modal
        title={
          <div>
            Order Details
            {selectedGroupInfo && (
              <div style={{ marginTop: 8 }}>
                <Tag color="blue" style={{ fontSize: 14 }}>
                  {selectedGroupInfo.material}
                </Tag>
                <Tag color="green" style={{ fontSize: 14 }}>
                  Width: {selectedGroupInfo.formatWidth}mm
                </Tag>
              </div>
            )}
          </div>
        }
        open={isOrderDetailsModalOpen}
        onCancel={() => setIsOrderDetailsModalOpen(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setIsOrderDetailsModalOpen(false)}>
            Close
          </Button>,
        ]}>
        <Table
          className="always-show-scrollbar"
          columns={orderDetailsColumns}
          dataSource={selectedGroupOrders}
          rowKey="orderNumber"
          size="small"
          pagination={false}
          scroll={{ y: 400, scrollToFirstRowOnChange: true }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                <Table.Summary.Cell index={0} colSpan={5}>
                  <strong>Total:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong>
                    {selectedGroupOrders
                      .reduce((sum, order) => sum + (order.labelCount || 0), 0)
                      .toLocaleString()}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <strong>
                    {selectedGroupOrders
                      .reduce((sum, order) => sum + (order.projectedMeters || 0), 0)
                      .toFixed(2)}{" "}
                    m
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Modal>
    </>
  )
}
